/**
 * Hook del listado de Locaciones con paginación server-side (US-2.2).
 *
 * Espejo de `useAllies`: filtros + `offset`/`limit` + `meta` del backend, con
 * loading/error/refresh. La red vive en `locationApi`; aquí solo el estado de la
 * vista (separable de la presentación).
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  AdminLocationSummary,
  ListLocationsQuery,
  PageMeta,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { locationApi, type LocationListParams } from '../api/locationApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_LOCATIONS_LIMIT = 20;

/** Filtros del listado (los del contrato, sin paginación). `status` incluye `ALL`. */
export type LocationFilters = Omit<ListLocationsQuery, 'offset' | 'limit'>;

export interface UseLocationsOptions {
  /** Elementos por página (default `DEFAULT_LOCATIONS_LIMIT`). */
  limit?: number;
  /** Filtros iniciales. */
  initialFilters?: LocationFilters;
}

export interface UseLocationsResult {
  /** Items resumidos del listado (sin `state`; el detalle lo trae). */
  data: AdminLocationSummary[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  /** Filtros activos. */
  filters: LocationFilters;
  /** Desplazamiento actual (paginación server-side). */
  offset: number;
  limit: number;
  /** Aplica filtros nuevos y reinicia la paginación a la primera página. */
  setFilters: (filters: LocationFilters) => void;
  /** Mueve la página (offset) actual. */
  setOffset: (offset: number) => void;
  /** Recarga la página actual (p. ej. tras crear/editar/eliminar una locación). */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar las ubicaciones.';
}

export function useLocations(options: UseLocationsOptions = {}): UseLocationsResult {
  const limit = options.limit ?? DEFAULT_LOCATIONS_LIMIT;
  const [filters, setFiltersState] = useState<LocationFilters>(() => options.initialFilters ?? {});
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<AdminLocationSummary[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: LocationListParams = { ...filters, offset, limit };
    locationApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [filters, offset, limit, reloadKey]);

  const setFilters = useCallback((next: LocationFilters) => {
    setOffsetState(0);
    setFiltersState(next);
  }, []);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, filters, offset, limit, setFilters, setOffset, refresh };
}
